import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, AuthService, ConfigService, LazyOktaService, SessionConflictService } from '@app-core/services';
import { clearBoundOktaSession } from '@app-core/services/session-conflict.service';
import { Dialog } from '@app-core/services/dialog';
import { RouterService } from '@app-core/services/router.service';
import { ClientConfigResponse } from '@app-model/api';
import { LoadQuote } from '@app-model/cart';
import { LoginRequest, OktaLoginRequest, UserResponse } from '@app-model/login';
import { IGetUsersRequest } from '@app-model/request';
import { GetUserResponse } from '@app-model/response';
import { UpdatePasswordPopupComponent } from '@app-shared/components/update-password/update-password-popup.component';
import * as RemitActions from '@app-store/remit/remit.actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as UserActions from './user.actions';
import { UpdatePendingNumberAction } from './user.actions';
import * as fromRoot from '../../store/reducers';
import * as CartActions from '../cart/cart.actions';
import { State } from '../config/config.reducer';
import * as ProductActions from '../product/product.actions';
import * as VehicleActions from '../vehicle/vehicle.actions';

@Injectable()
export class UserEffects {
  private _actions$ = inject(Actions);
  private _apiService = inject(ApiService);
  private _authService = inject(AuthService);
  private _configService = inject(ConfigService);
  private _dialog = inject(Dialog);
  private _oktaService = inject(LazyOktaService);
  private _router = inject(Router);
  private _routerService = inject(RouterService);
  private _sessionConflict = inject(SessionConflictService);
  private _store$ = inject<Store<fromRoot.State>>(Store);

  login$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.LOGIN),
      withLatestFrom(this._store$.select((state) => state.configState)),
      switchMap(([action, configState]: [UserActions.LogInAction, State]) => {
        const req: LoginRequest = {
          application: this._configService.appIdentifier,
          captcha: action.payload.captcha,
          captchaVersion: 'v3',
          password: action.payload.password,
          programId: configState.clientConfig.programId,
          username: action.payload.username,
        };

        return this._apiService.login(req).pipe(
          // If successful, dispatch success action with result
          map((response: UserResponse) => {
            this._configService.hideNewVersionMessage();

            // verify - update password page
            return {
              type: response.isTempPassword === 'Y' ? UserActions.UPDATE_PASSWORD : UserActions.LOGIN_SUCCESS,
              payload: { response, admin: action.payload.admin },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) => of({ type: UserActions.LOGIN_FAILURE, payload: error }))
        );
      })
    )
  );

  ssoOktaLogin$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.SSO_OKTA_LOGIN),
      switchMap((action: { type: string; payload: { oktaLoginRequest: OktaLoginRequest } }) =>
        this._apiService.getOktaUser(action.payload.oktaLoginRequest).pipe(
          map((response: UserResponse) => ({ type: UserActions.LOGIN_SUCCESS, payload: { response, admin: false } })),
          catchError((error) => of({ type: UserActions.SSO_OKTA_LOGIN_FAILURE, payload: error }))
        )
      )
    )
  );

  ssoOktaLoginFailure$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.SSO_OKTA_LOGIN_FAILURE),
        tap(() => this._showSsoWarning())
      ),
    { dispatch: false }
  );

  LogInSuccess$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.LOGIN_SUCCESS),
        tap((action: UserActions.LogInSuccessAction) => {
          // previously used to also set token in localStorage
          // but now used to just navigate if the login was successful
          localStorage.setItem('token', action['payload']['response']['token']);
          void this._oktaService.captureBoundIdpSession();
          const userState = localStorage.getItem('userState');
          if (userState) {
            const userStateObject = JSON.parse(userState);
            if (userStateObject.previousUser !== action['payload']['response']['userName']) {
              this._store$.dispatch(new CartActions.ResetCartAction());
              this._store$.dispatch(new ProductActions.ResetProductsAction());
              this._store$.dispatch(new VehicleActions.ResetProductFormAction());
            }
          }
          if (action.payload.admin) {
            void this._router.navigate(['/admin/console']);
          } else {
            this._routerService.navigateToContent();
          }

          if (action.payload.response.pendingContracts) {
            this._store$.dispatch(new UpdatePendingNumberAction(action['payload']['response']['pendingContracts']));
          }
        })
      ),
    { dispatch: false }
  );

  ValidateOktaPasswordResetToken$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.SSO_OKTA_VALIDATE_PASSWORD_RESET_TOKEN),
        switchMap((action: UserActions.SsoValidateOktaPasswordResetTokenreAction) =>
          this._apiService.validatePasswordResetToken(action.payload.token)
        ),
        tap(({ stateToken, username }) => {
          this._dialog.open(UpdatePasswordPopupComponent, {
            data: {
              stateToken,
              username,
              flowType: 'OKTA',
              actionType: 'RESET',
            },
          });
        })
      ),
    { dispatch: false }
  );

  UpdatePassword$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.UPDATE_PASSWORD),
        tap((action: UserActions.UpdatePasswordAction) => {
          // trigger popup for update password
          localStorage.setItem('token', action.payload.response.token);
          this._dialog.open(UpdatePasswordPopupComponent, {
            data: {
              username: action.payload.response.userName,
              flowType: 'LEGACY',
              actionType: 'UPDATE',
            },
          });
        })
      ),
    { dispatch: false }
  );

  LogOut$: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.LOGOUT),
      withLatestFrom(
        this._store$.select((state) => state.userState.user),
        this._store$.select((state) => state.userState.emulatedDealer)
      ),
      switchMap(([action, user, emulatedDealer]: [UserActions.LogOutAction, UserResponse, boolean]) => {
        if (user?.userName) {
          this._store$.dispatch(new UserActions.UpdatePreviousUserAction(user.userName));
        }

        if (emulatedDealer) {
          this._store$.dispatch(new UserActions.ResetSessionAction());
        }

        this._authService.cancelSessionTimer();
        this._authService.cancelTokenRenewal();
        localStorage.removeItem('token');
        clearBoundOktaSession();

        if (this._configService.environment.authorization.flow === 'okta' && action.payload.oktaLogout) {
          return this._oktaService.oktaSignOut().then(() => {
            this._router.navigate(['/login']);

            return { type: UserActions.LOGOUT_SUCCESS };
          }).catch(() => {
            this._router.navigate(['/login']);

            return { type: UserActions.LOGOUT_SUCCESS };
          });
        } else {
          this._router.navigate(['/login']);

          return of({ type: UserActions.LOGOUT_SUCCESS });
        }
      })
    )
  );

  ResetSession$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.RESET_SESSION),
        tap(() => {
          this._configService.hideErrorMessage();
          this._store$.dispatch(new VehicleActions.ResetProductFormAction());
          this._store$.dispatch(new ProductActions.ResetProductsAction());
          this._store$.dispatch(new CartActions.ResetCartAction());
        })
      ),
    { dispatch: false }
  );

  resetDealer$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.RESET_DEALER),
        tap(() => {
          this._store$.dispatch(new UserActions.ResetSessionAction());
          this._store$.dispatch(new RemitActions.GetPendingContractsAction());
          this._store$.dispatch(new UserActions.GetRecentQuotesAction());
        })
      ),
    { dispatch: false }
  );

  emulateDealer$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.EMULATE_DEALER),
        tap(() => {
          this._store$.dispatch(new UserActions.ResetSessionAction());
          this._store$.dispatch(new RemitActions.GetPendingContractsAction());
          this._store$.dispatch(new UserActions.GetRecentQuotesAction());
        })
      ),
    { dispatch: false }
  );

  UpdateVehicleInfo$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.UPDATE_VEHICLE_INFO),
        tap(() => {
          this._configService.hideErrorMessage();
          this._store$.dispatch(new ProductActions.ResetProductsAction());
          this._store$.dispatch(new CartActions.ResetCartAction());
        })
      ),
    { dispatch: false }
  );

  SaveMarkupToTopFilter$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.SAVE_MARKUP),
        withLatestFrom(this._store$.select((state) => state.userState.user)),
        tap(([action]: [UserActions.SaveMarkupAction, UserResponse]) =>
          this._store$.dispatch(new ProductActions.ChangeMarkupAction({ markup: action.payload }))
        )
      ),
    { dispatch: false }
  );

  saveMarkupApiCall$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.SAVE_MARKUP),
        withLatestFrom(this._store$.select((state) => state.userState.user)),
        switchMap(([action, user]: [UserActions.SaveMarkupAction, UserResponse]) =>
          this._apiService.saveMarkup(action.payload, user.userName)
        )
      ),
    { dispatch: false }
  );

  ssoLoginFailure$: Observable<any> = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.SSO_LOGIN_FAILURE),
        tap(() => this._showSsoWarning())
      ),
    { dispatch: false }
  );

  getRecentQuotes$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.GET_RECENT_QUOTES),
      withLatestFrom(this._store$.select((state) => state.userState.user)),
      switchMap(([, user]: [UserActions.GetRecentQuotesAction, UserResponse]) =>
        this._apiService.getRecentQuotes(user.cmsDealerNumber).pipe(
          // If successful, dispatch success action with result
          map((payload) => ({ type: UserActions.GET_RECENT_QUOTES_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: UserActions.GET_RECENT_QUOTES_SUCCESS, payload }))
        )
      )
    )
  );

  getQuote$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.GET_QUOTE),
      withLatestFrom(this._store$.select((store) => store.userState.user)),
      switchMap(([action, user]: [UserActions.GetQuoteAction, UserResponse]) => {
        const { dealer, vin } = action.payload;

        return this.callLoadQuote({ dealer, vin }, user).pipe(
          // If successful, dispatch success action with result
          map((payload: LoadQuote) => ({ type: CartActions.LOAD_QUOTE_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: UserActions.LOGIN_FAILURE, payload: {} }))
        );
      })
    )
  );

  deleteQuote$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.DELETE_QUOTE),
      switchMap((action: UserActions.DeleteQuoteAction) => {
        const { dealer, vin } = action.payload;

        return this._apiService.deleteQuote({ dealer, vin }).pipe(
          // If successful, dispatch success action with result
          map((payload: unknown) => ({ type: UserActions.DELETE_QUOTE_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: UserActions.LOGIN_FAILURE, payload: {} }))
        );
      })
    )
  );

  ssoLogin$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.SSO_LOGIN),
      switchMap(() =>
        this._apiService.ssoLogin({ application: this._configService.appIdentifier }).pipe(
          // If successful, dispatch success action with result
          map((response) => ({ type: UserActions.LOGIN_SUCCESS, payload: { response, admin: false } })),
          // If request fails, dispatch failed action
          catchError((payload) => {
            console.warn(payload);

            return of({ type: UserActions.SSO_LOGIN_FAILURE, payload });
          })
        )
      )
    )
  );

  getUsers$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.GETUSERS),
      withLatestFrom(
        this._store$.select((state) => state.userState.user),
        this._store$.select((state) => state.configState.clientConfig)
      ),
      switchMap(([, user, config]: [UserActions.GetUsersAction, UserResponse, ClientConfigResponse]) => {
        const req: IGetUsersRequest = {
          cmsDealerNumber: user.cmsDealerNumber,
          applicationId: user.applicationId,
          isProgramSSO: config.isProgramSSO,
          userSSOSuffix: config.userSSOSuffix,
        };

        return this._apiService.getUsers(req).pipe(
          // If successful, dispatch success action with result
          map((payload: GetUserResponse) => ({ type: UserActions.GETUSERS_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: UserActions.GETUSERS_FAILURE, payload }))
        );
      })
    )
  );

  private callLoadQuote(request: { dealer: string; vin: string }, user: UserResponse) {
    return user?.isRelatedDealer ? this._apiService.loadQuoteOnly(request) : this._apiService.loadQuote(request);
  }

  private _showSsoWarning(): void {
    this._sessionConflict.deferConflict();
    void this._router.navigate(['/callback/error']);
  }
}
